'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useEcomStoreSelector } from '@/data';
import { useSelectedStore } from '@/hooks';
import { GProduct, Order } from '@/types';
import {
  buildSearchBodyFromParams,
  parseInventoryLocationIds,
  SearchService,
} from '@/opensearch';
import { getDefaultStore, getProductsFromOpenSearchResponse } from '@/utils';
import { useFulFillmentType } from '@/hooks/useFulFillmentType';

interface IUseReorderProducts {
  hasStore: boolean;
  isLoading: boolean;
  products: GProduct[];
}

interface IUseReorderProductsOptions {
  fallbackToDefaultStore?: boolean;
}

export const getReorderItemIds = (order?: Order): string[] =>
  (order?.line_items ?? [])
    .map(item => item?.item_id?.trim())
    .filter((id): id is string => !!id);

/**
 * Which of the given products are available now in the user's CURRENT store, scoped by customer
 * type and inventory location, excluding out-of-stock / hidden items.
 */
export const useReorderProducts = (
  itemIds: string[],
  { fallbackToDefaultStore = false }: IUseReorderProductsOptions = {},
): IUseReorderProducts => {
  const { store: selectedStore } = useSelectedStore();
  const { customer_type, provider_inventory_location_id } =
    useEcomStoreSelector(['customer_type', 'provider_inventory_location_id']);
  const { fulfillmentType } = useFulFillmentType();

  const hasStore = !!selectedStore?.short_name;
  const store =
    selectedStore ?? (fallbackToDefaultStore ? getDefaultStore() : undefined);

  const ids = useMemo(() => itemIds, [itemIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const inventoryLocationIds = parseInventoryLocationIds(
    provider_inventory_location_id,
  );

  const { search } = new SearchService({
    entityId: store?.mso_store_entity_id,
    orgId: store?.mso_org_id,
    customerType: customer_type,
    isStrictMedicalEnabled: store?.strict_medical_enabled,
    inventoryLocationIds,
    fulfillmentType,
  });

  const canSearch = !!store?.short_name && ids.length > 0;

  const { data, isLoading } = useQuery({
    enabled: canSearch,
    queryKey: [
      'reorderProducts',
      store?.short_name,
      provider_inventory_location_id,
      customer_type,
      ...ids,
    ],
    queryFn: async () => {
      const { searchBody } = buildSearchBodyFromParams({
        searchParams: { id: ids },
        filters: {
          isAboveThreshold: [true],
          isHideFromMenu: [false],
        },
        limit: ids.length,
        omitAggs: true,
      });

      const result = await search({ searchBody });

      return getProductsFromOpenSearchResponse({ result });
    },
  });

  return {
    hasStore,
    isLoading: canSearch && isLoading,
    products: data ?? [],
  };
};
